import type { UpdateDataQuery } from "../../../../../components/Table/types/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { CampaignCopyEntity } from "../../types/CampaignCopyEntity";
import type { CampaignEntity } from "../../types/CampaignEntity";
import { CampaignCopyEntityToCopyDto } from "../mappers/CampaignCopyEntityToCopyDto";
import { CampaignDtoToEntity } from "../mappers/CampaignDtoToEntity";
import { CampaignEntityToDto } from "../mappers/CampaignEntityToDto";

const CampaignApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        CampaignLoad: builder.query<CampaignEntity, FormQuery>({
            providesTags: [ResourceName.CAMPAIGNS],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CAMPAIGNS}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return CampaignDtoToEntity(response);
            },
        }),
        CampaignSave: builder.mutation<CampaignEntity, FormMutation<CampaignEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CAMPAIGNS}`;
                const method = "POST";

                const body = CampaignEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CAMPAIGNS],
        }),
        CampaignUpdate: builder.mutation<CampaignEntity, FormMutation<CampaignEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CAMPAIGNS}/${mutation.data.id}`;
                const method = "PUT";

                const body = CampaignEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CAMPAIGNS],
        }),
        CampaignCopy: builder.mutation<CampaignEntity, FormMutation<CampaignCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CAMPAIGNS}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = CampaignCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CAMPAIGNS],
        }),
        campaign: builder.mutation<CampaignEntity, UpdateDataQuery>({
            query: ({ url }) => ({
                url,
                method: "POST",
                body: {},
            }),
            invalidatesTags: [ResourceName.CAMPAIGNS],
        }),
    }),
});

export const { useCampaignLoadQuery, useCampaignSaveMutation, useCampaignUpdateMutation, useCampaignCopyMutation, useCampaignMutation } =
    CampaignApi;
